import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { Plus, X } from "@phosphor-icons/react";
import { defaultSkill, skillSchema, type Skill } from "@reactive-resume/schema";
import {
  Badge,
  BadgeInput,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Slider,
} from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";

const formSchema = skillSchema;
const groupedSkillSchema = z.object({
  items: z.array(
    z.object({
      rowId: z.string(),
      name: z.string(),
      description: z.string(),
    }),
  ),
});
const defaultMastersUnionSkillNames = ["Business Skills", "Technical Skills"];

type FormValues = z.infer<typeof formSchema>;
type GroupedSkillValues = z.infer<typeof groupedSkillSchema>;

const createGroupedSkill = (name = "") => ({
  rowId: createId(),
  name,
  description: "",
});

export const SkillsDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultSkill,
    resolver: zodResolver(formSchema),
  });

  const groupedForm = useForm<GroupedSkillValues>({
    defaultValues: {
      items: defaultMastersUnionSkillNames.map((name) => createGroupedSkill(name)),
    },
    resolver: zodResolver(groupedSkillSchema),
  });

  const [pendingKeyword, setPendingKeyword] = useState("");
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const skills = useResumeStore((state) => state.resume.data.sections.skills.items);
  const setValue = useResumeStore((state) => state.setValue);
  const { isOpen, mode, close } = useDialog<Skill>("skills");
  const isMastersUnion = template === "mastersunion";

  const missingDefaultSkillNames = useMemo(() => {
    const existingSkillNames = new Set(skills.map((item) => item.name.trim().toLowerCase()));

    return defaultMastersUnionSkillNames.filter(
      (name) => !existingSkillNames.has(name.trim().toLowerCase()),
    );
  }, [skills]);

  const showGroupedCreateDialog =
    isMastersUnion && isOpen && mode === "create" && missingDefaultSkillNames.length > 0;

  const { fields, append, remove, replace } = useFieldArray({
    control: groupedForm.control,
    name: "items",
  });

  useEffect(() => {
    if (!showGroupedCreateDialog) return;

    replace(missingDefaultSkillNames.map((name) => createGroupedSkill(name)));
  }, [missingDefaultSkillNames, replace, showGroupedCreateDialog]);

  const onGroupedSubmit = (values: GroupedSkillValues) => {
    const nextItems = values.items
      .map((item) => ({
        ...defaultSkill,
        id: createId(),
        name: item.name.trim(),
        description: item.description
          .split("|")
          .map((value) => value.trim())
          .filter(Boolean)
          .join(" | "),
      }))
      .filter((item) => item.name && item.description);

    if (nextItems.length > 0) {
      setValue("sections.skills.items", [...skills, ...nextItems]);
    }

    close();
  };

  if (showGroupedCreateDialog) {
    return (
      <Dialog open={isOpen} onOpenChange={close}>
        <DialogContent className="z-50">
          <Form {...groupedForm}>
            <form className="space-y-6" onSubmit={groupedForm.handleSubmit(onGroupedSubmit)}>
              <DialogHeader>
                <DialogTitle>
                  <div className="flex items-center space-x-2.5">
                    {/* <Plus /> */}
                    <h2>Create skill groups</h2>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {fields.map((field, index) => {
                  const isDefaultSkillGroup = defaultMastersUnionSkillNames.includes(field.name);

                  return (
                    <div key={field.rowId} className="grid grid-cols-1 gap-4 rounded-lg border p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                          <FormField
                            name={`items.${index}.name`}
                            control={groupedForm.control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t`Name`}</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            name={`items.${index}.description`}
                            control={groupedForm.control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Excel | SQL | Power BI" />
                                </FormControl>
                                <FormDescription>
                                  Use | to separate individual skills.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        {!isDefaultSkillGroup && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(index)}
                          >
                            <X size={16} weight="bold" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <Button type="button" variant="secondary" onClick={() => append(createGroupedSkill())}>
                  Add More
                </Button>
              </div>

              <DialogFooter>
                <Button type="submit">Create</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <SectionDialog<FormValues>
      id="skills"
      form={form}
      defaultValues={defaultSkill}
      pendingKeyword={pendingKeyword}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Name`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Skill Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Description`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={isMastersUnion ? "Excel | SQL | Power BI" : undefined} />
              </FormControl>
              {isMastersUnion && (
                <FormDescription>Use | to separate individual skills.</FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {false && <FormField
          name="level"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Level`}</FormLabel>
              <FormControl className="py-2">
                <div className="flex items-center gap-x-4">
                  <Slider
                    {...field}
                    min={0}
                    max={5}
                    value={[field.value]}
                    orientation="horizontal"
                    onValueChange={(value) => field.onChange(value[0])}
                  />

                  {field.value === 0 ? (
                    <span className="text-base font-bold">{t`Hidden`}</span>
                  ) : (
                    <span className="text-base font-bold">{field.value}</span>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />}

        {false && <FormField
          name="keywords"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-3 sm:col-span-2">
              <FormItem>
                <FormLabel>{t`Keywords`}</FormLabel>
                <FormControl>
                  <BadgeInput {...field} setPendingKeyword={setPendingKeyword} />
                </FormControl>
                <FormDescription>
                  {t`You can add multiple keywords by separating them with a comma or pressing enter.`}
                </FormDescription>
                <FormMessage />
              </FormItem>

              <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                <AnimatePresence>
                  {field.value.map((item, index) => (
                    <motion.div
                      layout
                      key={item}
                      initial={{ opacity: 0, y: -50 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: index * 0.1 } }}
                      exit={{ opacity: 0, x: -50 }}
                    >
                      <Badge
                        className="cursor-pointer"
                        onClick={() => {
                          field.onChange(field.value.filter((v) => item !== v));
                        }}
                      >
                        <span className="mr-1">{item}</span>
                        <X size={12} weight="bold" />
                      </Badge>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        />}
      </div>
    </SectionDialog>
  );
};
